import React, { useState, useEffect } from 'react';  
import './Navbar.css';
import logo from '../assets/logo.png';
import { UserCircle, CaretDown, MagnifyingGlass, HeartStraight, List, X, Phone, ChatCircle } from "phosphor-react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SidebarAuth } from './SideBarAuth';
import { useSidebar } from '../context/SidebarContext';
import { useAuthContext } from '../context/AuthContext';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { useSearchNavigation } from '../hooks/useSearchNavigation';
import { useInterestList } from '../context/InterestListContext';

const Navbar = ({ search, setSearch, onCategoryFilter }) => {
  const { showSidebar, authMode, openSidebar, closeSidebar } = useSidebar();
  const { isAuthenticated, user } = useAuthContext();
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileProducts, setShowMobileProducts] = useState(false);
  const { getTotalItems } = useInterestList();
  const interestItems = getTotalItems();
  const scrollToTop = useScrollToTop();
  const location = useLocation();
  const navigate = useNavigate();
  const searchNavigation = useSearchNavigation(navigate, location, onCategoryFilter);

  useEffect(() => {
    // Fechar dropdowns quando clicar fora
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        closeDropdowns();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleProductsDropdown = () => {
    setShowProductsDropdown(!showProductsDropdown);
  };

  const closeDropdowns = () => {
    setShowProductsDropdown(false);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    if (showMobileMenu) {
      // Fechar submenus quando fechar menu principal
      setShowMobileProducts(false);
    }
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
    setShowMobileProducts(false);
  };

  const handleMobileProductsToggle = () => {
    setShowMobileProducts(!showMobileProducts);
  };

  // Fechar menu mobile quando redimensionar para desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        closeMobileMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Função para lidar com clique no logo
  const handleLogoClick = (e) => {
    // Se já estiver na página inicial, rolar para o topo
    if (location.pathname === '/') {
      e.preventDefault();
      scrollToTop();
    }
    // Se não estiver na página inicial, o Link fará a navegação normal
  };

  // Função para lidar com mudanças na barra de pesquisa
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    
    // Usar o hook para navegação automática
    searchNavigation(value);
  };

  // Função para lidar com clique no ícone de pesquisa
  const handleSearchIconClick = () => {
    // Se houver texto na pesquisa ou não estiver na página shop, navegar para shop
    if (search.trim().length > 0 || location.pathname !== '/shop') {
      if (onCategoryFilter) {
        onCategoryFilter('');
      }
      navigate('/shop');
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={handleLogoClick}>
          <img src={logo} alt="MercadoTV" />
        </Link>
        
        <div className="navbar-categories">
          <Link to="/" className="navbar-link">
            Início
          </Link>
          
          <div className="dropdown-container">
            <button 
              className="dropdown-btn" 
              onClick={handleProductsDropdown}
            >
              Produtos
              <CaretDown size={16} />
            </button>
            {showProductsDropdown && (
              <div className="dropdown-menu">
                <Link 
                  to="/shop" 
                  className="dropdown-item dropdown-link"
                  onClick={() => {
                    onCategoryFilter('');
                    closeDropdowns();
                  }}
                >
                  Todos os Produtos
                </Link>
                <Link 
                  to="/shop"
                  className="dropdown-item" 
                  onClick={() => {
                    onCategoryFilter('Videovigilância');
                    closeDropdowns();
                  }}
                >
                  Videovigilância
                </Link>
                <Link 
                  to="/shop"
                  className="dropdown-item" 
                  onClick={() => {
                    onCategoryFilter('Alarmes');
                    closeDropdowns();
                  }}
                >
                  Alarmes
                </Link>
                <Link 
                  to="/shop"
                  className="dropdown-item" 
                  onClick={() => {
                    onCategoryFilter('Videoporteiros');
                    closeDropdowns();
                  }}
                >
                  Videoporteiros
                </Link>
                <Link 
                  to="/shop"
                  className="dropdown-item" 
                  onClick={() => {
                    onCategoryFilter('Acessórios');
                    closeDropdowns();
                  }}
                >
                  Acessórios
                </Link>
              </div>
            )}
          </div>

          <Link to="/orcamento" className="navbar-link">
            Pedir Orçamento
          </Link>

          <Link to="/contactos" className="navbar-link">
            Contacte-nos
          </Link>
        </div>
        
        <div className="navbar-search">
          <input
            type="text"
            placeholder="Pesquisar produtos..."
            value={search}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              // Enter também navega para shop se necessário
              if (e.key === 'Enter') {
                handleSearchIconClick();
              }
            }}
          />
          <button 
            className="search-icon-btn"
            onClick={handleSearchIconClick}
            title="Ir para produtos"
          >
            <MagnifyingGlass size={18} />
          </button>
        </div>

        <div className="navbar-icons">
          <button className="icon user-button" onClick={() => openSidebar('login')} title={isAuthenticated && user ? `Olá, ${user.username}` : 'Entrar'}>
            <UserCircle size={20} />
          </button>
          <Link to="/lista-interesse" className="icon interest-icon" title="Lista de Interesse">
            <HeartStraight size={20} weight="fill" />
            {interestItems > 0 && (
              <span className="interest-badge">{interestItems}</span>
            )}
          </Link>
          
          {/* Menu Hambúrguer */}
          <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
            {showMobileMenu ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Barra de Pesquisa Mobile dentro da navbar */}
      <div className="navbar-search-mobile">
        <input
          type="text"
          placeholder="Pesquisar produtos..."
          value={search}
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearchIconClick();
            }
          }}
        />
        <button 
          className="navbar-search-mobile-btn"
          onClick={handleSearchIconClick}
          title="Pesquisar"
        >
          <MagnifyingGlass size={20} />
        </button>
      </div>

      {/* Menu Mobile Overlay */}
      {showMobileMenu && (
        <div className={`mobile-menu-overlay ${showMobileMenu ? 'open' : ''}`} onClick={closeMobileMenu}>
          <div className={`mobile-menu ${showMobileMenu ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
            {/* Header do Menu Mobile */}
            <div className="mobile-menu-header">
              <button className="mobile-menu-close" onClick={closeMobileMenu}>
                <X size={24} />
              </button>
            </div>

            {/* Menu Items */}
            <div className="mobile-menu-items">
              <Link to="/" className="mobile-menu-item" onClick={closeMobileMenu}>
                Início
              </Link>

              {/* Produtos com submenu */}
              <div className="mobile-menu-item-container">
                <button 
                  className="mobile-menu-item mobile-menu-expandable"
                  onClick={handleMobileProductsToggle}
                >
                  Produtos
                  <CaretDown size={16} className={showMobileProducts ? 'rotated' : ''} />
                </button>
                {showMobileProducts && (
                  <div className="mobile-submenu">
                    <Link 
                      to="/shop" 
                      className="mobile-submenu-item"
                      onClick={() => {
                        onCategoryFilter('');
                        closeMobileMenu();
                      }}
                    >
                      Todos os Produtos
                    </Link>
                    <Link 
                      to="/shop"
                      className="mobile-submenu-item" 
                      onClick={() => {
                        onCategoryFilter('Videovigilância');
                        closeMobileMenu();
                      }}
                    >
                      Videovigilância
                    </Link>
                    <Link 
                      to="/shop"
                      className="mobile-submenu-item" 
                      onClick={() => {
                        onCategoryFilter('Alarmes');
                        closeMobileMenu();
                      }}
                    >
                      Alarmes
                    </Link>
                    <Link 
                      to="/shop"
                      className="mobile-submenu-item" 
                      onClick={() => {
                        onCategoryFilter('Videoporteiros');
                        closeMobileMenu();
                      }}
                    >
                      Videoporteiros
                    </Link>
                    <Link 
                      to="/shop"
                      className="mobile-submenu-item" 
                      onClick={() => {
                        onCategoryFilter('Acessórios');
                        closeMobileMenu();
                      }}
                    >
                      Acessórios
                    </Link>
                  </div>
                )}
              </div>

              <Link to="/orcamento" className="mobile-menu-item" onClick={closeMobileMenu}>
                Pedir Orçamento
              </Link>

              <Link to="/contactos" className="mobile-menu-item" onClick={closeMobileMenu}>
                Contacte-nos
              </Link>

              <Link to="/lista-interesse" className="mobile-menu-item" onClick={closeMobileMenu}>
                Lista de Interesse
                {interestItems > 0 && (
                  <span className="mobile-interest-badge">{interestItems}</span>
                )}
              </Link>
            </div>

            {/* Botões de Contacto Rápido */}
            <div className="mobile-quick-contact">
              <a href="tel:256418202" className="mobile-contact-btn mobile-contact-phone">
                <Phone size={20} />
                <span>Ligar Agora</span>
              </a>
              <a href="https://wa.me/351256418202" className="mobile-contact-btn mobile-contact-whatsapp" target="_blank" rel="noopener noreferrer">
                <ChatCircle size={20} />
                <span>WhatsApp</span>
              </a>
            </div>

            {/* Rodapé do Menu */}
            <div className="mobile-menu-footer">
              <button 
                className="mobile-login-btn" 
                onClick={() => {
                  openSidebar('login');
                  closeMobileMenu();
                }}
              >
                <UserCircle size={20} />
                {isAuthenticated && user ? `Olá, ${user.username}` : 'Entrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SidebarAuth 
        open={showSidebar} 
        onClose={closeSidebar} 
        authMode={authMode} 
      />
    </nav>
  );
};

export default Navbar;
